import { useEffect, useRef } from "react";

import { PROMOTIONS_ENABLED } from "@/features/promotions/config";
import { sendPromotionEvent } from "@/features/promotions/utils/promotion-events";

export const PROMOTION_VISIBLE_RATIO = 0.5;
export const PROMOTION_VISIBLE_MS = 1000;

/** Continuous visibility: leaving the viewport or backgrounding resets the full second. */
export const observePromotion = (
  element: Element,
  onImpression: () => void
) => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let visible = false;
  let sent = false;
  const clear = () => {
    if (timer) {
      clearTimeout(timer);
    }

    timer = undefined;
  };
  const schedule = () => {
    clear();

    if (!visible || document.visibilityState !== "visible" || sent) {
      return;
    }

    timer = setTimeout(() => {
      sent = true;
      onImpression();
    }, PROMOTION_VISIBLE_MS);
  };
  const observer = new IntersectionObserver(
    entries => {
      visible = entries.some(
        entry =>
          entry.target === element &&
          entry.isIntersecting &&
          entry.intersectionRatio >= PROMOTION_VISIBLE_RATIO
      );
      schedule();
    },
    { threshold: [0, PROMOTION_VISIBLE_RATIO] }
  );
  observer.observe(element);
  document.addEventListener("visibilitychange", schedule);
  return () => {
    clear();
    observer.disconnect();
    document.removeEventListener("visibilitychange", schedule);
  };
};

export const usePromotionObservation = (token?: string | null) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (
      !PROMOTIONS_ENABLED ||
      !token ||
      !ref.current ||
      typeof IntersectionObserver === "undefined"
    ) {
      return;
    }

    return observePromotion(ref.current, () => {
      void sendPromotionEvent(token, "impression");
    });
  }, [token]);

  return {
    ref,
    trackClick: () => {
      if (token) {
        void sendPromotionEvent(token, "click");
      }
    },
  };
};
